import { useEffect, useState, useCallback, useMemo, createContext, useContext } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SiteContentContext = createContext({ settings: {}, loaded: false, refresh: () => {} });

/**
 * Provides dynamic site settings (key -> {sq, en} or {value}).
 * Exposes refresh() so admin save flows can force a refetch and the public
 * pages see updated content immediately without a full reload.
 */
export const SiteContentProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site-settings`);
      setSettings(data || {});
    } catch {
      // keep previous settings on transient failure
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Refetch whenever the tab regains focus / visibility — covers the case where
  // the user edits content in one tab and returns to a public tab.
  useEffect(() => {
    const onFocus = () => { refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);

  const value = useMemo(() => ({ settings, loaded, refresh }), [settings, loaded, refresh]);

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => useContext(SiteContentContext);

/**
 * Returns translated text for a key. Falls back to provided default if missing.
 * Usage: useText("home.hero.title", "Default title")
 */
export const useText = (key, fallbackByLang = { sq: "", en: "" }, currentLang = "sq") => {
  const { settings } = useSiteContent();
  const v = settings[key];
  if (v && typeof v === "object") {
    if (v[currentLang]) return v[currentLang];
    if (v.value !== undefined) return v.value;
  }
  return fallbackByLang[currentLang] !== undefined ? fallbackByLang[currentLang] : fallbackByLang;
};
